import _ from "lodash";
import { isIcV2 } from "pages/pages/preview/icV2/helper";
import { SLIDE } from "constants/widgetTypes";

const CONTENT_FIELDS = [
  ["content", "content"],
  ["name", "name"],
  ["description", "description"],
  ["properties.desktop.title", "title"],
  ["properties.desktop.description", "description"],
  ["properties.desktop.label", "label"],
  ["properties.desktop.placeholder", "placeholder"],
  ["properties.desktop.question", "question"],
  ["properties.desktop.content", "content"],
  ["properties.desktop.text", "text"],
  ["properties.desktop.html", "text"],
  ["properties.desktop.alt", "imageAltText"],
  ["properties.desktop.altText", "imageAltText"],
  ["properties.desktop.caption", "caption"],
  ["properties.desktop.href", "link"],
];

const plainText = (value) => {
  if (value == null || typeof value === "object") return "";
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
};

const widgetTypeLabel = (type) => {
  const name = String(type || "content")
    .replace(/^cleverstory\.v2\.widgets\./, "")
    .replace(/\./g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");
  return name.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const semanticContent = (widget) =>
  CONTENT_FIELDS.reduce((result, [path, label]) => {
    const value = plainText(_.get(widget, path));
    if (value && !Object.values(result).includes(value)) result[label] = value;
    return result;
  }, {});

const semanticWidget = (widget, children, position) => ({
  position,
  contentType: widgetTypeLabel(widget.type),
  ...semanticContent(widget),
  ...(children.length ? { children } : {}),
});

const byAscendingOrder = (a, b) =>
  (Number(a.order) || 0) - (Number(b.order) || 0) ||
  String(a.uid).localeCompare(String(b.uid));

const byDescendingOrder = (a, b) =>
  (Number(b.order) || 0) - (Number(a.order) || 0) ||
  String(b.uid).localeCompare(String(a.uid));

const siblingOrder = (siblings) =>
  siblings.every((widget) => widget.type === SLIDE)
    ? byAscendingOrder
    : byDescendingOrder;

const isLoadingPlaceholder = (widget) =>
  String(widget?.uid || "")
    .toLowerCase()
    .includes("widget_loading");

const isDesktopHidden = (widget) =>
  _.get(widget, "properties.desktop.visible", true) === false;

const descendantsOf = (widgets, rootUid) => {
  if (!rootUid) return widgets;
  const included = new Set([rootUid]);
  let changed = true;
  while (changed) {
    changed = false;
    widgets.forEach((widget) => {
      if (included.has(widget.parent) && !included.has(widget.uid)) {
        included.add(widget.uid);
        changed = true;
      }
    });
  }
  return widgets.filter((widget) => included.has(widget.uid));
};

const withoutHiddenWidgets = (widgets) => {
  const hidden = new Set(
    widgets.filter(isDesktopHidden).map((widget) => widget.uid)
  );

  let changed = true;
  while (changed) {
    changed = false;
    widgets.forEach((widget) => {
      if (hidden.has(widget.parent) && !hidden.has(widget.uid)) {
        hidden.add(widget.uid);
        changed = true;
      }
    });
  }

  return widgets.filter((widget) => !hidden.has(widget.uid));
};

const buildContentTree = (widgets, rootUid) => {
  const selected = withoutHiddenWidgets(
    descendantsOf(widgets, rootUid).filter(
      (widget) => !isLoadingPlaceholder(widget)
    )
  );
  const selectedIds = new Set(selected.map((widget) => widget.uid));
  const childrenByParent = new Map();

  selected.forEach((widget) => {
    const parent =
      widget.uid === rootUid || !selectedIds.has(widget.parent)
        ? null
        : widget.parent;
    const siblings = childrenByParent.get(parent) || [];
    siblings.push(widget);
    childrenByParent.set(parent, siblings);
  });

  const buildChildren = (parent) => {
    const siblings = (childrenByParent.get(parent) || []).slice();
    return siblings
      .sort(siblingOrder(siblings))
      .map((widget, index) =>
        semanticWidget(widget, buildChildren(widget.uid), index + 1)
      );
  };

  return buildChildren(null);
};

export const withPersistedAlias = (aliases, persisted) => {
  if (!persisted?.id) return aliases || [];
  const list = [...(aliases || [])];
  const index = list.findIndex((item) => item.id === persisted.id);
  if (index >= 0) {
    list[index] = { ...list[index], ...persisted };
    return list;
  }
  return [persisted, ...list];
};

export const resolveCanonicalAlias = (page, aliases) => {
  const storyboardType = _.get(page, "storyboard.type");
  if (isIcV2(storyboardType)) {
    return (
      page?.subDirectory ||
      page?.canonical ||
      _.get(page, "properties.desktop.alias") ||
      ""
    );
  }

  const canonicalAlias = (aliases || []).find((item) => item?.canonical)?.url;
  return (
    canonicalAlias ||
    page?.canonical ||
    page?.subDirectory ||
    _.get(page, "properties.desktop.alias") ||
    ""
  );
};

export const applyDraftPageMeta = (page, draft) => {
  if (!draft) return page;

  return {
    ...page,
    name: draft.name != null ? draft.name : page?.name,
    draft: {
      ...page?.draft,
      seo: {
        ..._.get(page, "draft.seo"),
        name: draft.title != null ? draft.title : _.get(page, "draft.seo.name"),
        description:
          draft.description != null
            ? draft.description
            : _.get(page, "draft.seo.description"),
      },
    },
  };
};

export const buildSchemaOutline = (page, widgets, rootUid, aliases) => {
  const pageName = plainText(page?.name);
  const outline = {
    page: {
      name: pageName,
      canonical: resolveCanonicalAlias(page, aliases),
      title: plainText(page?.draft?.seo?.name) || pageName,
      description: plainText(page?.draft?.seo?.description),
    },
    contentInVisualOrder: buildContentTree(widgets, rootUid),
  };
  return JSON.stringify(outline);
};

export const currentSchemaHash = (page, widgets, rootUid, aliases) =>
  hashSchemaOutline(buildSchemaOutline(page, widgets, rootUid, aliases));

export const isAutoSchemaStale = (seo, page, widgets, rootUid, aliases) => {
  if (_.get(seo, "schemaMarkupSource") !== "auto") return false;
  if (!_.get(seo, "schemaMarkup")) return false;
  return (
    _.get(seo, "schemaContentHash") !==
    currentSchemaHash(page, widgets, rootUid, aliases)
  );
};

export const hashSchemaOutline = (outline) => {
  let hash = 5381;
  for (let index = 0; index < outline.length; index += 1) {
    hash = ((hash << 5) + hash) ^ outline.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
};

export const parseSchemaMarkup = (value) => {
  const parsed = JSON.parse(value);
  const hasType =
    typeof parsed?.["@type"] === "string" ||
    (Array.isArray(parsed?.["@graph"]) && parsed["@graph"].length > 0);
  if (
    !parsed ||
    Array.isArray(parsed) ||
    parsed["@context"] !== "https://schema.org" ||
    !hasType
  ) {
    throw new Error(
      'Schema must be a JSON object with "@context": "https://schema.org" and an @type or @graph.'
    );
  }
  return parsed;
};

export const isValidSchemaMarkup = (value) => {
  if (!value?.trim()) return false;
  try {
    parseSchemaMarkup(value);
    return true;
  } catch {
    return false;
  }
};
